'use client'

import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import YouTubeIcon from '@mui/icons-material/YouTube'
import { useLocale, useTranslations } from 'next-intl'
import React, { useEffect, useState } from 'react'

import FlyinLogo from '@/src/assets/svgs/FlyinLogo'
import VatBadge from '@/src/components/common/footer/VatBadge'
import ThreadsIcon from '@/src/components/common/icons/ThreadsIcon'
import TwitterIcon from '@/src/components/common/icons/TwitterIcon'
import isGlobalDomain from '@/src/utils/generalUtils/isGlobalDomain'
import { getSupportCardsImageSrc } from '@/src/utils/supportCards/getSupportCardsImageSrc'
import { SvgIconComponent } from '@mui/icons-material'
import { Box, Container, IconButton, Link as MuiLink, Typography } from '@mui/material'
import Image from 'next/image'
import { getSupportHref } from '@/src/utils/header/headerUtils'

const FlightScheduleFooter: React.FC<{ hostname?: string }> = ({ hostname }) => {
    const locale = useLocale()
    const t = useTranslations('footer')

    const [showTravelLicense, setShowTravelLicense] = useState(false)
    const [currentYear, setCurrentYear] = useState<number | null>(null)
    const supportCardsImageSrc = getSupportCardsImageSrc({ locale, hostname })

    useEffect(() => {
        setShowTravelLicense(isGlobalDomain())
        setCurrentYear(new Date().getFullYear())
    }, [locale])
    // Social media links configuration
    const socialMediaLinks = [
        {
            name: 'youtube',
            href: 'https://www.youtube.com/user/FlyinVideos',
            icon: YouTubeIcon
        },
        {
            name: 'twitter',
            href: 'https://twitter.com/flyincom',
            icon: 'twitter' // Custom SVG
        },
        {
            name: 'instagram',
            href: 'https://www.instagram.com/flyincom/',
            icon: InstagramIcon
        },
        {
            name: 'facebook',
            href: 'https://www.facebook.com/flyin.com',
            icon: FacebookIcon
        },
        {
            name: 'linkedin',
            href: 'https://www.linkedin.com/company/flyin-com',
            icon: LinkedInIcon
        },
        {
            name: 'threads',
            href: 'https://www.threads.net/@flyincom',
            icon: 'threads'
        }
    ]

    // Company links
    const companyLinks = [
        {
            label: t('aboutUs'),
            href: `/aboutUs.${locale}.html`
        },
        {
            label: t('faqs'),
            href: `/faqs.${locale}.html`
        },
        {
            label: t('privacyPolicy'),
            href: `/privacyPolicy.${locale}.html`
        },
        {
            label: t('termsOfUse'),
            href: `/termsOfUse.${locale}.html`
        }
    ]

    // Support links
    const walletHref = `/${locale}/wallet`
    const supportLinks = [
        {
            label: t('flyinWallet'),
            href: walletHref
        },
        {
            label: t('contactUs'),
            href: getSupportHref(locale)
        },
        {
            label: t('addYourProperty'),
            href: `/addProperty.${locale}.html`
        },
        {
            label: t('fileClaim'),
            href: `/howTo.${locale}.html`
        }
    ]

    return (
        <Box component="footer" sx={{ bgcolor: 'grey.100', pt: 4, pb: 3 }}>
            <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
                {/* Top Section: Logo + License Info + Social Icons */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        pb: 3
                    }}
                >
                    {/* Left: Logo and License Info */}
                    <Box>
                        {/* Logo */}
                        <Box sx={{ mb: 2 }}>
                            <FlyinLogo sx={{ width: 100, height: 42 }} />
                        </Box>

                        {/* License Information */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {showTravelLicense && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: '13px',
                                        lineHeight: 1.6
                                    }}
                                >
                                    {t('travelLicense')}:{' '}
                                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                        73100559
                                    </Box>
                                </Typography>
                            )}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '13px',
                                    lineHeight: 1.6
                                }}
                            >
                                {t('licenseCategory')}:{' '}
                                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    {t('travelAndTourismAgency')}
                                </Box>
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '13px',
                                    lineHeight: 1.6
                                }}
                            >
                                {t('registrationNumber')}:{' '}
                                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    7001623003
                                </Box>
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right: Social Media Icons */}
                    <Box
                        component="nav"
                        aria-label="Social media"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            {socialMediaLinks.map(social => (
                                <IconButton
                                    key={social.name}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={social.name}
                                    sx={{
                                        bgcolor: 'common.white',
                                        width: 48,
                                        height: 48,
                                        borderRadius: '12%',
                                        '&:hover': { bgcolor: 'grey.200', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }
                                    }}
                                >
                                    {social.icon === 'twitter' ? (
                                        <TwitterIcon sx={{ color: 'text.primary', fontSize: 24 }} />
                                    ) : social.icon === 'threads' ? (
                                        <ThreadsIcon sx={{ color: 'text.primary', fontSize: 24 }} />
                                    ) : (
                                        React.createElement(social.icon as SvgIconComponent, {
                                            sx: { color: 'text.primary', fontSize: 24 }
                                        })
                                    )}
                                </IconButton>
                            ))}
                        </Box>
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: 348,
                                borderRadius: 1,
                                p: 1,
                                bgcolor: 'grey.300',
                                display: { xs: 'none', md: 'block' },
                                overflow: 'hidden'
                            }}
                        >
                            <Image
                                src={supportCardsImageSrc}
                                alt={t('support')}
                                width={348}
                                height={48}
                                loading="lazy"
                                style={{ width: '100%', height: 'auto', display: 'block' }}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Divider */}
                <Box sx={{ height: '1px', bgcolor: 'divider', my: 3 }} />

                {/* Bottom Section: Links + VAT + Copyright */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    {/* Left: Navigation Links */}
                    <Box component="nav" aria-label="Footer links">
                        <Box
                            component="ul"
                            sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', m: 0, p: 0, listStyle: 'none' }}
                        >
                            {companyLinks.map((link, index) => (
                                <Box component="li" key={index}>
                                    <MuiLink
                                        href={link.href}
                                        sx={{
                                            color: 'text.secondary',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                            textTransform: 'none',
                                            '&:hover': { color: 'primary.main' }
                                        }}
                                    >
                                        {link.label}
                                    </MuiLink>
                                </Box>
                            ))}
                            {supportLinks
                                .filter(link => link.label === t('fileClaim'))
                                .map((link, index) => (
                                    <Box component="li" key={`file-claim-${index}`}>
                                        <MuiLink
                                            href={link.href}
                                            sx={{
                                                color: 'text.secondary',
                                                textDecoration: 'none',
                                                fontSize: '14px',
                                                textTransform: 'none',
                                                '&:hover': { color: 'primary.main' }
                                            }}
                                        >
                                            {link.label}
                                        </MuiLink>
                                    </Box>
                                ))}
                        </Box>
                    </Box>

                    {/* Right: VAT Logo + Copyright */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <VatBadge size={40} />
                        {currentYear && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px', fontWeight: 400 }}>
                                {t('copyright', { year: currentYear })}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export { FlightScheduleFooter }
export default FlightScheduleFooter
